// @flow

import DomUtils from 'app/common/utils/dom';
import { defaultDataroomSessionForWidget } from 'app/document-index-widget/constant';

type DataroomSession = {
  allowQnaAttachments: boolean,
  customWatermarksEnabled: boolean,
  dataRoomProjectName: string,
  dataRoomUseSod: boolean,
  dataRoomUseTermsOfAccess: boolean,
  dataroomId: number,
  documentSecurityEnabled: boolean,
  fileExtensionAllowOrDeny: string,
  fileExtensionBarDelimitedList: string,
  isAnsaradaSuperUserProfile: boolean,
  isAnsaradaUserProfile: boolean,
  isAnsaradaAdminManagementAccessAllowed: boolean,
  isDemoDataRoom: boolean,
  isUserIpAccessListAllowed: boolean,
  maxAllowedQuestionLength: number,
  qnaEnabled: boolean,
  qnaWorkflowCode: string,
  sydneyOffsetMinutes: number,
  translationTagNameAdminSide: string,
  translationTagNameNonAdminSide: string,
  transactionTypeId: number,
  transactionTypeName: string,
  useSecurity2: boolean,
  userAccessLogId: number,
  userId: number,
  userTypeCode: string,
  userProfileId: number,
  dataRoomName: string,
  showMyAccount: boolean,
  userProfileFullName: string,
  userProfileEmail: string,
  bulkDownloadMaxSizeLimitBytes: number,
  bulkDownloadMaxItemsCountLimit: number,
  disableDownloadWhenSavingDenied: boolean,
  useMultipleSubmissionWindows: boolean,
  hasFirstGuestUserLoggedIn: boolean,
  isTenderSubmissionAvailable: boolean,
  evaluationToolEnabled: boolean,
};

type DataRoomInitData = {
  currentDataRoomDate: string,
  currentSydneyDate: string,
  copyToClipboardUrl: string,
  fullTextSearchUrl: string,
  askQuestionUrl: string,
  documentOfficeViewerUrl: string,
  viewDocumentFileUrl: string,
  documentIndexExcelExportUrl: string,
  documentViewerUrl: string,
  archivesUrl: string,
  webBulkUploadUrl: string,
  repairNumberingUrl: string,
  restoreDeletedDocumentsUrl: string,
  webGenerateFoldersUrl: string,
  webBulkUpdateUrl: string,
  moveFolderUrl: string,
  moveDocumentUrl: string,
  createOrEditFolderUrl: string,
  createOrEditDocumentUrl: string,
  folderHistoryUrl: string,
  editSecurityUrl: string,
  openEnableMultipleUrl: string,
  manageBatchDocumentsFoldersUrl: string,
  manageDocumentsExcelUrl: string,
  documentSearchUrl: string,
  showEmptyFolders: boolean,
  manageBidderTeamsUrl: string,
  importFolderStructureTemplateUrl: string,
};

type Feature2Data = {
  'in-895-send-to-kira': boolean,
  'ten-580-add-collapse-all-expand-all-for-submission-index-expander': boolean,
  'DIL-892-migrate-self-service-archive-list-and-request-self-service-archive-forms-from-MVC-to-SPA': boolean,
  'dil-846-add-truncation-settings-selector-for-create-self-service-archive-ui': boolean,
  'deals-38-add-evaluation-attachments-in-self-archive': boolean,
  'dil-921-the-what-can-they-view-features-sometimes-show-all-documents-regardless-of-the-view-as-filter': boolean,
  'dil-979-start-using-new-bulk-copy-api-to-copy-multiple-docs-folders-at-the-same-time': boolean,
  'dil-1115-disable-fetching-entire-doc-index-and-runtime-calculations-for-bulk-downloads-on-doc-index': boolean,
  'deals-52-dil-document-management-event-tracking': boolean,
  'dil-984-enable-ms-office-online-integration-for-data-room': boolean,
  'DIL-1206-disable-calculate-for-expandabl': boolean,
  'dil-1285-secure-file-sharing-with-valid-email': boolean,
  'dil-1284-secure-file-sharing-with-passcode': boolean,
  'dil-1528-hide-send-to-kira-button-in-data-room': boolean,
  'DIL-1549-spa-show-sharing-icon-for-getdocindex': boolean,
  'dil-1650-allow-to-save-redaction-overwriting-the-original': boolean,
  'dil-1952-bulk-redaction-add-redactions-panel': boolean,
  'dil-2059-bulk-redaction-ui-in-manage-docs': boolean,
  'dil-2832-add-bulk-redaction-button-to-the-actions-menu-on-documents-page': boolean,
  'dil-3137-bulk-update-tool-in-manage-docs-for-admins': boolean,
  'DIL-3314-documents-spa-customize-add-documents-from-data-room-UI-for-CS-Admin-users': boolean,
  'dil-3588-make-ms-office-online-the-default-click-to-open-action-for-doc-index-and-manage-docs-for-supported-file-types': boolean,
  'DIL-3913-documents-spa-disable-ner-calling': boolean,
  'DIL-4651-data-room-crashing-in-big-room': boolean,
  'dil-150-sunset-the-legacy-action-menu-buttons': boolean,
  'dil-4824-disable-underline-entities-in-single-redaction': boolean,
  'dil-4819-create-new-filter-component-with-nds-components': boolean,
  'dil-4693-smart-document-translation': boolean,
  'dil-4980-smash-it-2024-bulk-redaction-performance-improvements': boolean,
  'dil-5061-ability-to-identify-and-restrict-moved-documents': boolean,
  'DEALS-1183-Include-Workflow-data-in-Archives': boolean,
};

type AppConf = {
  apiBase: string,
  basename: string,
};

class DataroomService {
  session: DataroomSession;

  feature2Data: Feature2Data;

  appConf: AppConf;

  initData: DataRoomInitData;

  getAllScripts() {
    this.initData = DomUtils.getScriptData<DataRoomInitData>(
      '#dataroom-init-data',
    );
    this.session = DomUtils.getScriptData<DataroomSession>(
      '#dataroom-session-data',
    );

    this.feature2Data = DomUtils.getScriptData<Feature2Data>('#feature2-data');
    this.appConf = DomUtils.getScriptData<AppConf>('#ansarada-react-config');
  }

  getSpaBaseName() {
    if (!this.session) {
      return '';
    }

    let result;
    const pathnameRegex = new RegExp('/_mvc/(.*)/spa/documents');

    if (window.location.pathname.match(pathnameRegex)) {
      const roomPath = window.location.pathname.match(pathnameRegex)[1];

      result = `/_mvc/${roomPath}/spa/documents`;
    }

    result = `/_mvc/${this.session.dataRoomProjectName}%7C${this.session.dataroomId}/${this.session.userId}/spa/documents`;
    return result;
  }

  isAdmin() {
    return this.session.userTypeCode === 'ADM';
  }

  initForWidget(
    userAccessLogId: number,
    userId: number,
    userTypeCode: string,
    userProfileId: number,
    dataroomId: number,
    dataRoomProjectName: string,
    feature2Data: Feature2Data,
    initData: Object,
  ) {
    this.initData = initData;

    this.session = {
      ...defaultDataroomSessionForWidget,
      userAccessLogId,
      userId,
      userTypeCode,
      userProfileId,
      dataroomId,
      dataRoomProjectName,
    };

    this.feature2Data = feature2Data;
  }
}

const dataroomService = new DataroomService();

export type { DataroomSession, Feature2Data };

export default dataroomService;
